import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, Button, Pill, Loader } from "@/components/ui/Primitives";
import { Building2, Link2, RefreshCw, CheckCircle2, XCircle, AlertCircle, FileText, Home, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { usePageTitle } from "@/lib/seo";

const fmtCAD = (cents) =>
  `CA$${((cents || 0) / 100).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function CondoWorksIntegration() {
  usePageTitle("CondoWorks integration — Central Rentals");
  const [status, setStatus] = useState(null);
  const [units, setUnits] = useState([]);
  const [invoices, setInvoices] = useState({ items: [], total_open_cents: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({ connect: false, sync_units: false, sync_invoices: false, disconnect: false });
  const [form, setForm] = useState({ api_key: "", workspace_id: "", workspace_name: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [s, u, i] = await Promise.all([
        api.get("/condoworks/status").then((r) => r.data),
        api.get("/condoworks/units").then((r) => r.data).catch(() => ({ items: [] })),
        api.get("/condoworks/invoices").then((r) => r.data).catch(() => ({ items: [], total_open_cents: 0 })),
      ]);
      setStatus(s);
      setUnits(u.items || []);
      setInvoices(i);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const connect = async (e) => {
    e.preventDefault();
    if (!form.api_key || !form.workspace_id) {
      toast.error("Enter your CondoWorks API key + workspace ID.");
      return;
    }
    setBusy((b) => ({ ...b, connect: true }));
    try {
      const { data } = await api.post("/condoworks/connect", form);
      toast.success(data.sandbox ? "Sandbox connected — synthetic data ready." : `Connected to ${data.workspace_name}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not connect");
    } finally { setBusy((b) => ({ ...b, connect: false })); }
  };

  const syncUnits = async () => {
    setBusy((b) => ({ ...b, sync_units: true }));
    try {
      const { data } = await api.post("/condoworks/sync/units");
      toast.success(`Synced ${data.count} unit${data.count === 1 ? "" : "s"}.`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
    } finally { setBusy((b) => ({ ...b, sync_units: false })); }
  };

  const syncInvoices = async () => {
    setBusy((b) => ({ ...b, sync_invoices: true }));
    try {
      const { data } = await api.post("/condoworks/sync/invoices");
      toast.success(`Synced ${data.count} invoice${data.count === 1 ? "" : "s"}.`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
    } finally { setBusy((b) => ({ ...b, sync_invoices: false })); }
  };

  const disconnect = async () => {
    if (!confirm("Disconnect CondoWorks? Your synced units and invoices will remain visible, but no new data will be pulled.")) return;
    setBusy((b) => ({ ...b, disconnect: true }));
    try {
      await api.post("/condoworks/disconnect");
      toast.success("Disconnected.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not disconnect");
    } finally { setBusy((b) => ({ ...b, disconnect: false })); }
  };

  const payInvoice = async (id) => {
    try {
      await api.post(`/condoworks/invoices/${id}/pay`);
      toast.success("Marked paid.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not mark paid");
    }
  };

  if (loading) return <Loader/>;

  const connected = !!status?.connected;

  return (
    <div className="space-y-6" data-testid="condoworks-page">
      <PageHeader
        title="CondoWorks"
        subtitle="Sync your condo units, board invoices, and payments alongside your rental portfolio."
      />

      {/* Connection card */}
      {!connected ? (
        <Card className="p-6" data-testid="cw-connect-card">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-11 h-11 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center flex-none">
              <Link2 className="w-5 h-5"/>
            </div>
            <div>
              <h2 className="font-display text-lg font-bold text-slate-900">Connect your CondoWorks workspace</h2>
              <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                Paste your CondoWorks API key + workspace ID. We&apos;ll validate the credentials with CondoWorks and pull your units and open invoices. To try the flow first, use the API key <code className="px-1.5 py-0.5 rounded bg-slate-100 text-[12px]">sandbox_demo</code> — we&apos;ll populate 5 sample condo units and 6 sample invoices.
              </p>
            </div>
          </div>
          <form onSubmit={connect} className="space-y-3 max-w-lg">
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-600">CondoWorks API key</label>
              <input
                type="password"
                value={form.api_key}
                onChange={(e) => setForm({ ...form, api_key: e.target.value })}
                placeholder="sandbox_demo or cw_live_..."
                className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-sm"
                data-testid="cw-api-key"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-600">Workspace ID</label>
              <input
                type="text"
                value={form.workspace_id}
                onChange={(e) => setForm({ ...form, workspace_id: e.target.value })}
                placeholder="ws_..."
                className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-sm"
                data-testid="cw-workspace-id"
              />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-600">Workspace label (optional)</label>
              <input
                type="text"
                value={form.workspace_name}
                onChange={(e) => setForm({ ...form, workspace_name: e.target.value })}
                placeholder="e.g. Bay Street Towers Board"
                className="mt-1 w-full px-3 py-2.5 rounded-xl border border-slate-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 outline-none text-sm"
                data-testid="cw-workspace-name"
              />
            </div>
            <Button type="submit" disabled={busy.connect} data-testid="cw-connect-btn">
              <Link2 className="w-4 h-4"/> {busy.connect ? "Connecting…" : "Connect CondoWorks"}
            </Button>
          </form>
        </Card>
      ) : (
        <Card className="p-6 bg-gradient-to-br from-emerald-50 to-white border-emerald-200" data-testid="cw-connected-card">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center flex-none">
                <CheckCircle2 className="w-5 h-5"/>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="font-display text-lg font-bold text-slate-900">Connected</h2>
                  {status.sandbox && <Pill tone="amber">Sandbox</Pill>}
                </div>
                <p className="text-sm text-slate-600 mt-1">Workspace: <b>{status.workspace_name || status.workspace_id}</b></p>
                {status.last_sync && <p className="text-xs text-slate-500 mt-0.5">Last sync: {new Date(status.last_sync).toLocaleString("en-CA")}</p>}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={syncUnits} disabled={busy.sync_units} data-testid="cw-sync-units">
                <RefreshCw className={`w-4 h-4 ${busy.sync_units ? "animate-spin" : ""}`}/> Sync units
              </Button>
              <Button onClick={syncInvoices} disabled={busy.sync_invoices} data-testid="cw-sync-invoices">
                <RefreshCw className={`w-4 h-4 ${busy.sync_invoices ? "animate-spin" : ""}`}/> Sync invoices
              </Button>
              <Button variant="secondary" onClick={disconnect} disabled={busy.disconnect} data-testid="cw-disconnect">
                <XCircle className="w-4 h-4"/> Disconnect
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Summary stats */}
      {connected && (
        <div className="grid md:grid-cols-3 gap-3">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <Home className="w-4 h-4 text-slate-500"/>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Condo units</div>
                <div className="font-display text-2xl font-extrabold text-slate-900" data-testid="cw-summary-units">{units.length}</div>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <FileText className="w-4 h-4 text-slate-500"/>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Open invoices</div>
                <div className="font-display text-2xl font-extrabold text-slate-900" data-testid="cw-summary-invoices">{invoices.items.filter((i) => (i.status || "").toLowerCase() === "open").length}</div>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <DollarSign className="w-4 h-4 text-slate-500"/>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Total due</div>
                <div className="font-display text-2xl font-extrabold text-slate-900" data-testid="cw-summary-total">{fmtCAD(invoices.total_open_cents || 0)}</div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Units */}
      {connected && (
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="w-4 h-4 text-slate-600"/>
            <h3 className="font-display font-bold text-slate-900">Condo units</h3>
          </div>
          {units.length === 0 ? (
            <div className="text-sm text-slate-500">No units synced yet — click <b>Sync units</b> above.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="cw-units-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                    <th className="py-2">Building</th>
                    <th>Unit</th>
                    <th>Beds/Baths</th>
                    <th>Sq Ft</th>
                    <th>Monthly fee</th>
                    <th>Reserve</th>
                    <th>Prov.</th>
                  </tr>
                </thead>
                <tbody>
                  {units.map((u) => (
                    <tr key={u.condoworks_id} className="border-b border-slate-100" data-testid={`cw-unit-${u.condoworks_id}`}>
                      <td className="py-2 font-medium text-slate-900">{u.building_name}</td>
                      <td>{u.unit_number}</td>
                      <td>{u.beds ?? "—"}/{u.baths ?? "—"}</td>
                      <td>{u.sqft ?? "—"}</td>
                      <td>{fmtCAD(u.monthly_fee_cents)}</td>
                      <td>{fmtCAD(u.reserve_balance_cents)}</td>
                      <td><Pill tone="slate">{u.province || "—"}</Pill></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Invoices */}
      {connected && (
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-4 h-4 text-slate-600"/>
            <h3 className="font-display font-bold text-slate-900">Board invoices</h3>
          </div>
          {invoices.items.length === 0 ? (
            <div className="text-sm text-slate-500">No invoices synced yet — click <b>Sync invoices</b> above.</div>
          ) : (
            <div className="divide-y divide-slate-100" data-testid="cw-invoices-list">
              {invoices.items.map((inv) => {
                const isPaid = (inv.status || "").toLowerCase() === "paid";
                return (
                  <div key={inv.condoworks_id} className="py-3 flex items-center justify-between gap-4 flex-wrap" data-testid={`cw-invoice-${inv.condoworks_id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-semibold text-slate-900">{inv.vendor_name}</div>
                        <Pill tone={isPaid ? "green" : "amber"}>{inv.status}</Pill>
                        {inv.category && <Pill tone="slate">{inv.category}</Pill>}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">{inv.description}</div>
                      <div className="text-xs text-slate-500 mt-0.5">Due {inv.due_date}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-display font-bold text-slate-900">{fmtCAD(inv.amount_cents)}</div>
                      {!isPaid && (
                        <button onClick={() => payInvoice(inv.condoworks_id)}
                          className="text-xs font-semibold text-emerald-700 hover:text-emerald-900 mt-1"
                          data-testid={`cw-pay-${inv.condoworks_id}`}>
                          Mark paid
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {!connected && (
        <Card className="p-5 bg-slate-50 border-slate-200">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-4 h-4 text-slate-500 mt-0.5"/>
            <p className="text-sm text-slate-600 leading-relaxed">
              Not sure where to find your API key? Log in to CondoWorks → <b>Settings → Integrations → API keys</b>. Copy the key that starts with <code className="px-1.5 py-0.5 rounded bg-slate-100 text-[12px]">cw_live_</code>. Your workspace ID is in <b>Settings → Workspace → Details</b>.
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}
